import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { ConfigModel } from "../../api/config/ConfigModel";
import { ConfigAdapter } from "../../api/config/ConfigAdapter";
import type { ZekayConfig, ConfigAssetUploadResponse } from "../../api/config/Config.typed";

interface ConfigContextType {
  getConfig: () => Promise<ZekayConfig | undefined>;
  updateConfig: (cfg: ZekayConfig) => Promise<void>;
  uploadConfigAsset: (
    file: Blob,
    name?: string,
    field?: "logo" | "logo_qr",
  ) => Promise<ConfigAssetUploadResponse | undefined>;
  deleteConfigAsset: (field: "logo" | "logo_qr") => Promise<boolean>;
  getGoogleLabels: () => Promise<string[] | undefined>;
}

const ConfigContext = React.createContext<ConfigContextType>(null!);

export function ConfigProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new ConfigModel(appConfig);
    return new ConfigAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>, successMsg?: string): Promise<T | undefined> => {
      try {
        const result = await fn();
        if (successMsg) showAlert(successMsg, "success");
        return result;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const getConfig = React.useCallback(
    async (): Promise<ZekayConfig | undefined> =>
      adapter ? wrap(() => adapter.getConfig()) : undefined,
    [adapter, wrap],
  );

  const updateConfig = React.useCallback(
    async (cfg: ZekayConfig): Promise<void> => {
      if (!adapter) return;
      try {
        await adapter.updateConfig(cfg);
        showAlert("Configuration mise à jour", "success");
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
      }
    },
    [adapter, showAlert],
  );

  const uploadConfigAsset = React.useCallback(
    async (
      file: Blob,
      name?: string,
      field?: "logo" | "logo_qr",
    ): Promise<ConfigAssetUploadResponse | undefined> =>
      adapter
        ? wrap(() => adapter.uploadConfigAsset(file, name, field), "Asset envoyé")
        : undefined,
    [adapter, wrap],
  );

  const deleteConfigAsset = React.useCallback(
    async (field: "logo" | "logo_qr"): Promise<boolean> => {
      if (!adapter) return false;
      try {
        await adapter.deleteConfigAsset(field);
        showAlert(field === "logo_qr" ? "Logo QR supprimé" : "Logo supprimé", "success");
        return true;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Erreur", "danger");
        return false;
      }
    },
    [adapter, showAlert],
  );

  const getGoogleLabels = React.useCallback(
    async (): Promise<string[] | undefined> =>
      adapter ? wrap(() => adapter.getGoogleLabels()) : undefined,
    [adapter, wrap],
  );

  const value = React.useMemo<ConfigContextType>(
    () => ({
      getConfig,
      updateConfig,
      uploadConfigAsset,
      deleteConfigAsset,
      getGoogleLabels,
    }),
    [getConfig, updateConfig, uploadConfigAsset, deleteConfigAsset, getGoogleLabels],
  );

  return <ConfigContext.Provider value={value}>{props.children}</ConfigContext.Provider>;
}

export function useConfig(): ConfigContextType {
  const ctx = React.useContext(ConfigContext);
  if (!ctx) throw new Error("useConfig must be used within ConfigProvider");
  return ctx;
}
